import store from '../store/store';

const DEFAULT_GOOGLE_API_KEY = "AIzaSyByQ7gGqL2jGh-y_flN_pQGg_hIrUJ_luQ";

const getApiKey = () => {
    const settings = store.getState()?.settingsdata?.settings;
    return settings?.googleApiKeys?.server || settings?.googleApiKeys?.web || DEFAULT_GOOGLE_API_KEY;
};

export const fetchPlacesAutocomplete = (searchKeyword, sessionToken) => {
    return new Promise(async (resolve, reject) => {
        const settings = store.getState()?.settingsdata?.settings;
        const apiKey = getApiKey();
        const url = 'https://places.googleapis.com/v1/places:autocomplete';
        const body = {
            input: searchKeyword,
            ...(sessionToken ? { sessionToken } : {})
        };
        if (settings?.restrictCountry) {
            body.includedRegionCodes = [settings.restrictCountry];
        }
        if (settings?.mapLanguage) {
            body.languageCode = settings.mapLanguage;
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': apiKey,
                    'X-Goog-FieldMask': 'suggestions.placePrediction.text,suggestions.placePrediction.placeId'
                },
                body: JSON.stringify(body)
            });
            const json = await response.json();
            if (json.suggestions) {
                const results = json.suggestions.map(s => ({
                    place_id: s.placePrediction?.placeId,
                    description: s.placePrediction?.text?.text || ''
                }));
                resolve(results);
            } else {
                resolve([]);
            }
        } catch (error) {
            console.log('fetchPlacesAutocomplete error:', error);
            reject("fetchPlacesAutocomplete Call Error");
        }
    });
};

export const fetchCoordsfromPlace = (place_id, addressFallback) => {
    return new Promise(async (resolve, reject) => {
        const settings = store.getState()?.settingsdata?.settings;
        const apiKey = getApiKey();
        const langParam = settings?.mapLanguage ? `?languageCode=${settings.mapLanguage}` : '';
        const url = `https://places.googleapis.com/v1/places/${place_id}${langParam}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': apiKey,
                    'X-Goog-FieldMask': 'id,displayName,location'
                }
            });
            const json = await response.json();
            if (json.location && json.location.latitude && json.location.longitude) {
                return resolve({
                    lat: json.location.latitude,
                    lng: json.location.longitude
                });
            }
        } catch (e) {
            console.log('Places (New) API failed, falling back to geocode API:', e);
        }

        // Fallback 1: Geocode by place_id
        try {
            const geoUrl = `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place_id}&key=${apiKey}`;
            const geoRes = await fetch(geoUrl);
            const geoJson = await geoRes.json();
            if (geoJson.results && geoJson.results.length > 0 && geoJson.results[0].geometry?.location) {
                return resolve({
                    lat: geoJson.results[0].geometry.location.lat,
                    lng: geoJson.results[0].geometry.location.lng
                });
            }
        } catch (e) {
            console.log('Geocode place_id fallback failed:', e);
        }

        // Fallback 2: Geocode by address string
        if (addressFallback) {
            try {
                const addrUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressFallback)}&key=${apiKey}`;
                const addrRes = await fetch(addrUrl);
                const addrJson = await addrRes.json();
                if (addrJson.results && addrJson.results.length > 0 && addrJson.results[0].geometry?.location) {
                    return resolve({
                        lat: addrJson.results[0].geometry.location.lat,
                        lng: addrJson.results[0].geometry.location.lng
                    });
                }
            } catch (e) {
                console.log('Geocode address fallback failed:', e);
            }
        }

        reject("Location not found");
    });
};

export const fetchAddressfromCoords = (latlng) => {
    return new Promise(async (resolve, reject) => {
        const apiKey = getApiKey();
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latlng}&key=${apiKey}`;
        try {
            const response = await fetch(url);
            const json = await response.json();
            if (json.results && json.results.length > 0 && json.results[0].formatted_address) {
                resolve(json.results[0].formatted_address);
            } else {
                reject(json.error_message || "No address found");
            }
        } catch (error) {
            console.log('fetchAddressfromCoords error:', error);
            reject("fetchAddressfromCoords Call Error");
        }
    });
};

export const getDistanceMatrix = (startLoc, destLoc) => {
    return new Promise(async (resolve, reject) => {
        const apiKey = getApiKey();
        const url = 'https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix';
        const startLat = parseFloat(startLoc.lat || (typeof startLoc === 'string' ? startLoc.split(',')[0] : 0));
        const startLng = parseFloat(startLoc.lng || (typeof startLoc === 'string' ? startLoc.split(',')[1] : 0));
        
        let destArray = Array.isArray(destLoc) ? destLoc : [destLoc];
        const destinations = destArray.map(loc => ({
            waypoint: {
                location: {
                    latLng: {
                        latitude: parseFloat(loc.lat || (typeof loc === 'string' ? loc.split(',')[0] : 0)),
                        longitude: parseFloat(loc.lng || (typeof loc === 'string' ? loc.split(',')[1] : 0))
                    }
                }
            }
        }));

        const body = {
            origins: [{
                waypoint: {
                    location: {
                        latLng: {
                            latitude: startLat,
                            longitude: startLng
                        }
                    }
                }
            }],
            destinations: destinations,
            travelMode: 'DRIVE',
            routingPreference: 'TRAFFIC_AWARE'
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': apiKey,
                    'X-Goog-FieldMask': 'originIndex,destinationIndex,duration,distanceMeters,status,condition'
                },
                body: JSON.stringify(body)
            });
            const json = await response.json();
            if (Array.isArray(json) && json.length > 0) {
                const resBody = json.map(item => ({
                    found: item.condition === "ROUTE_EXISTS",
                    distance_in_km: item.distanceMeters ? (item.distanceMeters / 1000) : 0,
                    time_in_secs: parseInt(item.duration?.split('s')[0]) || 0,
                    timein_text: `${Math.round((parseInt(item.duration?.split('s')[0]) || 0) / 60)} mins`
                }));
                resolve(resBody);
            } else {
                resolve([{ found: false, distance_in_km: 0, time_in_secs: 0, timein_text: '0 mins' }]);
            }
        } catch (error) {
            console.log('getDistanceMatrix error:', error);
            reject("getDistanceMatrix Call Error");
        }
    });
};

export const getDirectionsApi = (startLoc, destLoc, waypoints) => {
    return new Promise(async (resolve, reject) => {
        const apiKey = getApiKey();
        const url = 'https://routes.googleapis.com/directions/v2:computeRoutes';
        const startLat = parseFloat(startLoc.lat || (typeof startLoc === 'string' ? startLoc.split(',')[0] : 0));
        const startLng = parseFloat(startLoc.lng || (typeof startLoc === 'string' ? startLoc.split(',')[1] : 0));
        const destLat = parseFloat(destLoc.lat || (typeof destLoc === 'string' ? destLoc.split(',')[0] : 0));
        const destLng = parseFloat(destLoc.lng || (typeof destLoc === 'string' ? destLoc.split(',')[1] : 0));

        const body = {
            origin: { location: { latLng: { latitude: startLat, longitude: startLng } } },
            destination: { location: { latLng: { latitude: destLat, longitude: destLng } } },
            travelMode: 'DRIVE'
        };

        if (waypoints && waypoints.length > 0) {
            body.intermediates = waypoints.map(wp => ({
                location: {
                    latLng: {
                        latitude: parseFloat(wp.lat || (typeof wp === 'string' ? wp.split(',')[0] : 0)),
                        longitude: parseFloat(wp.lng || (typeof wp === 'string' ? wp.split(',')[1] : 0))
                    }
                }
            }));
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': apiKey,
                    'X-Goog-FieldMask': 'routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline'
                },
                body: JSON.stringify(body)
            });
            const json = await response.json();
            if (json.routes && json.routes.length > 0) {
                const route = json.routes[0];
                resolve({
                    distance_in_km: route.distanceMeters / 1000,
                    time_in_secs: route.duration ? route.duration.split('s')[0] : 0,
                    polylinePoints: route.polyline?.encodedPolyline || ''
                });
            } else {
                reject(json.error?.message || "No routes found");
            }
        } catch (error) {
            console.log('getDirectionsApi error:', error);
            reject("getDirectionsApi Call Error");
        }
    });
};
